import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Lock } from "lucide-react";
import { hasAdminAccess } from "../../utils/roleAccess";

/**
 * Closes a page off while it is still being finished.
 *
 * Christian asked (Monday 12780827140, 15 Aug 2026) for Search, Market Update,
 * CMA and AI Voice Caller to be closed so agents cannot get into them yet, with
 * a SOON pill in the sidebar. Hiding the sidebar row is not enough on its own —
 * Search in particular sat under Properties for months, so agents have bookmarks
 * — hence this guard on the route itself.
 *
 * Admins are let through on purpose: Christian said he has edits coming for the
 * Market Update and CMA pages, and he cannot review a page he cannot open. This
 * matches how the Buyer Portal gates its own "Soon" pages. Dev accounts come
 * through the same door, since an unfinished page is exactly what the tech
 * team needs to open.
 *
 * `allowPropertiesOnlyPa` is for the Public Property Search: a PA on
 * `properties_only` access has no Prospecting tab at all and that page is the
 * only one their role can reach, so closing it would leave them with a blank CRM.
 */
const ComingSoonRoute = ({ children, title, allowPropertiesOnlyPa = false }) => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const isAdmin = hasAdminAccess(user);
  const isPropertiesOnlyPA =
    user?.role === "pa" && user?.pa_access_level === "properties_only";

  if (isAdmin || (allowPropertiesOnlyPa && isPropertiesOnlyPA)) {
    return children;
  }

  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center bg-white border border-gray-100 rounded-2xl shadow-sm p-8">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="w-6 h-6 text-amber-600" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-gray-900">
          {title} is coming soon
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          This page is still being finished off. It will open up to the team
          shortly.
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-6 px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: "#55C5D0" }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default ComingSoonRoute;
